import { Box, lighten, useTheme } from '@mui/material';
import { ChartTooltip } from '../common/ChartTooltip/ChartTooltip';
import { ToolTipLine, ToolTipRound } from './CalculatorLineChart.styles';
import { useMemo } from 'react';
import ChartLegends from '../common/ChartLegends/ChartLegends';
import LineChart from '../LineChart/LineChart';
import { NivoGridContainer } from '../common/NivoGridContainer';
import AutoSizer from 'react-virtualized-auto-sizer';
import Flex from '@components/Flex/Flex';
import { LineChartProps, PointTooltipProps } from './CalculatorLineChart.types';
import { Config } from '@config/config';
import { useIntl } from 'react-intl';
import GlobalIntlSingleton from '@setup/intl/GlobalIntlSingleton';

const formatYAxisNumber = (number: number, digits: number) => {
  return GlobalIntlSingleton.intl.formatNumber(number, {
    notation: 'compact',
    maximumFractionDigits: 0,
  });
};

const formatXAxisNumber = (value: number, max: number): string => {
  if (max > 20) {
    return value % 2 !== 0 ? `${value}` : '';
  }
  if (max > 15) {
    return `${value}`;
  }
  return `${value}Y`;
};

const CalculatorLineChart: React.FC<LineChartProps> = ({
  title,
  yTooltipData,
  data,
  ...restProps
}: LineChartProps) => {
  const yValues = useMemo(() => {
    const maxInvestmentAmount = Math.round(
      Number(data?.[0]?.data[data?.[0]?.data?.length - 1].y)
    );
    const maxValueCreation = Math.round(
      Number(data?.[2]?.data[data?.[2]?.data?.length - 1].y)
    );
    const maxAdditionalContribution = Math.round(
      Number(data?.[1]?.data[data?.[1]?.data?.length - 1].y)
    );
    if (data?.[1]?.data[1]?.additionalContribution === 0) {
      return [0, maxInvestmentAmount, maxValueCreation];
    }
    return [
      0,
      maxInvestmentAmount,
      maxAdditionalContribution,
      maxValueCreation,
    ];
  }, [data]);

  const theme = useTheme();
  const intl = useIntl();
  const { sortedColors } = useMemo(() => {
    const sortedColors = data.map((_x, index) =>
      lighten(theme.palette.primary.main, (1 / data.length) * index)
    );

    return { sortedColors: sortedColors.reverse() };
  }, [data, theme.palette.primary.main]);

  return (
    <NivoGridContainer>
      <ChartLegends title={title} data={data} colors={sortedColors} />
      <Flex flex='3 0 auto'>
        <AutoSizer>
          {({ height, width }) => (
            <LineChart
              height={height}
              width={width}
              theme={{
                axis: {
                  ticks: {
                    line: {
                      stroke: theme.palette.grey[500],
                    },
                    text: {
                      fill: theme.palette.grey.A100,
                      fontWeight: 600,
                    },
                  },
                },
              }}
              margin={{
                top: 40,
                left: 20,
                right: 40,
                bottom: 40,
              }}
              data={data}
              colors={sortedColors}
              areaOpacity={1}
              tooltip={(pointData: PointTooltipProps) => (
                <Box mb={16}>
                  <ChartTooltip
                    title={''}
                    items={[
                      {
                        label: intl.formatMessage({
                          id: 'product.calculator.startingAmount',
                        }),
                        value: intl.formatNumber(
                          data?.[2].data[Number(pointData.point.data.x)]
                            .initialInvestment,
                          {
                            style: 'currency',
                            currency: Config.currency.code,
                            minimumFractionDigits: 0,
                          }
                        ),
                      },
                      {
                        label: intl.formatMessage({
                          id: 'product.calculator.additionalContributions',
                        }),
                        value: intl.formatNumber(
                          data?.[2].data[Number(pointData.point.data.x)]
                            .additionalContribution,
                          {
                            style: 'currency',
                            currency: Config.currency.code,
                            minimumFractionDigits: 0,
                          }
                        ),
                      },
                      {
                        label: intl.formatMessage({
                          id: 'product.calculator.interestAccrued',
                        }),
                        value: intl.formatNumber(
                          data?.[2].data[Number(pointData.point.data.x)]
                            .valueCreation,
                          {
                            style: 'currency',
                            currency: Config.currency.code,
                            minimumFractionDigits: 0,
                          }
                        ),
                      },
                      {
                        label: intl.formatMessage(
                          {
                            id: 'product.calculator.yearEndBalance',
                          },
                          { year: pointData.point.data.x }
                        ),
                        value: intl.formatNumber(
                          Number(
                            data?.[2].data[Number(pointData.point.data.x)].y
                          ),
                          {
                            style: 'currency',
                            currency: Config.currency.code,
                            minimumFractionDigits: 0,
                          }
                        ),
                      },
                    ]}
                    anchor={
                      Number(pointData.point.id.split('.')?.[1]) >=
                      data[data.length - 1].data.length / 2
                        ? 'right'
                        : 'left'
                    }
                  />
                  <ToolTipLine />
                  <ToolTipRound />
                </Box>
              )}
              enableGridY={false}
              lineWidth={0}
              axisBottom={{
                tickSize: 0,
                tickPadding: 14,
                format: (v) =>
                  v !== 0 ? formatXAxisNumber(v, data[0].data.length) : v,
              }}
              enableArea
              enableCrosshair={false}
              axisRight={{
                legendOffset: 12,
                tickValues: yValues,
                tickSize: 0,
                format: (v) => (v !== 0 ? `$${formatYAxisNumber(v, 0)}` : v),
              }}
              axisLeft={null}
              {...restProps}
            />
          )}
        </AutoSizer>
      </Flex>
    </NivoGridContainer>
  );
};

export default CalculatorLineChart;
